import {
  AlertCircle,
  Bot,
  Check,
  ChevronDown,
  Circle,
  Loader2,
  MessageCircleQuestion
} from 'lucide-react'

import { Tabs, TabsList, TabsTrigger } from '../ui/tabs'
import { Button } from '../ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../ui/dropdown-menu'
import { cn } from '../../lib/utils'
import { LEAD_PILL_KEY, type IngestAgentPill, type IngestAgentPillStatus } from './ingestAgentPills'

function isSubagent(pill: IngestAgentPill): boolean {
  return pill.agentKind === 'ingest_section_parser' || pill.agentKind === 'ingest_reconciler'
}

function subagentKindLabel(pill: IngestAgentPill): string {
  return pill.agentKind === 'ingest_reconciler' ? 'Reconciler' : 'Section agent'
}

function StatusIcon({ status }: { status: IngestAgentPillStatus }) {
  switch (status) {
    case 'waiting_user':
      return <MessageCircleQuestion className="size-3.5 text-st-warn" aria-hidden />
    case 'running':
      return <Loader2 className="size-3.5 animate-spin text-primary" aria-hidden />
    case 'pending':
      return <Circle className="size-3.5 text-muted-foreground/70" aria-hidden />
    case 'done':
      return <Check className="size-3.5 text-st-run" aria-hidden />
    case 'failed':
    case 'aborted':
      return <AlertCircle className="size-3.5 text-st-down" aria-hidden />
    default:
      return <Circle className="size-3.5 text-muted-foreground" aria-hidden />
  }
}

function PillIcon({ pill }: { pill: IngestAgentPill }) {
  if (!isSubagent(pill)) return <StatusIcon status={pill.status} />
  if (pill.status === 'running') {
    return <Loader2 className="size-3.5 animate-spin text-primary" aria-hidden />
  }
  if (pill.status === 'waiting_user') {
    return <MessageCircleQuestion className="size-3.5 text-st-warn" aria-hidden />
  }
  const tint =
    pill.status === 'done'
      ? 'text-st-run'
      : pill.status === 'failed' || pill.status === 'aborted'
        ? 'text-st-down'
        : 'text-muted-foreground'
  return <Bot className={cn('size-3.5', tint)} aria-hidden />
}

function statusLabelClass(status: IngestAgentPillStatus): string | undefined {
  switch (status) {
    case 'waiting_user':
      return 'text-st-warn'
    case 'running':
      return 'text-primary'
    case 'done':
      return 'text-st-run'
    case 'failed':
    case 'aborted':
      return 'text-st-down'
    default:
      return undefined
  }
}

function isTerminal(status: IngestAgentPillStatus): boolean {
  return status === 'done' || status === 'failed' || status === 'aborted'
}

/** Active agents stay as pills; finished / not-started children collapse into the dropdown. */
function isActivePill(pill: IngestAgentPill, selectedKey: string): boolean {
  if (pill.key === selectedKey) return true
  if (pill.key === LEAD_PILL_KEY) return true
  if (pill.status === 'running' || pill.status === 'waiting_user') return true
  return false
}

function pillTitle(pill: IngestAgentPill): string {
  if (pill.status === 'pending') return `${pill.label} — not started yet`
  if (isSubagent(pill)) return `${subagentKindLabel(pill)} · ${pill.label}`
  return pill.label
}

interface AgentPillStripProps {
  pills: readonly IngestAgentPill[]
  selectedKey: string
  onSelect: (key: string) => void
}

function PillTrigger({ pill, selectedKey }: { pill: IngestAgentPill; selectedKey: string }) {
  const idle = pill.status === 'pending' && pill.key !== selectedKey
  const subagent = isSubagent(pill)
  return (
    <TabsTrigger
      value={pill.key}
      title={pillTitle(pill)}
      className={cn(
        'group/agent-pill h-auto max-w-[14rem] flex-none gap-1.5 rounded-md border px-2 py-1 text-left text-xs font-medium whitespace-nowrap transition-colors after:hidden hover:bg-muted/60',
        'data-[state=active]:border-primary/40 data-[state=active]:font-medium data-[state=active]:text-foreground data-[state=active]:shadow-none',
        'group-data-[variant=line]/tabs-list:data-[state=active]:bg-primary/10',
        'dark:group-data-[variant=line]/tabs-list:data-[state=active]:bg-primary/10 dark:data-[state=active]:border-primary/40 dark:data-[state=active]:text-foreground',
        idle
          ? 'border-transparent bg-transparent text-muted-foreground'
          : 'border-border bg-card text-foreground',
        subagent &&
          !idle &&
          'border-dashed data-[state=active]:border-solid data-[state=active]:border-primary/40',
        pill.status === 'waiting_user' &&
          'data-[state=inactive]:border-st-warn/50 data-[state=inactive]:bg-st-warn-bg/40',
        pill.status === 'done' &&
          'data-[state=inactive]:border-st-run/40 data-[state=inactive]:bg-st-run-bg/30',
        (pill.status === 'failed' || pill.status === 'aborted') &&
          'data-[state=inactive]:border-st-down/40 data-[state=inactive]:bg-st-down-bg/30'
      )}
    >
      <PillIcon pill={pill} />
      <span className="flex min-w-0 items-baseline gap-1">
        <span className={cn('min-w-0 truncate', statusLabelClass(pill.status))}>{pill.label}</span>
        {subagent ? (
          <span className="text-10 font-normal text-muted-foreground">
            {pill.agentKind === 'ingest_reconciler' ? 'reconciler' : 'agent'}
          </span>
        ) : null}
      </span>
    </TabsTrigger>
  )
}

/**
 * Horizontal strip of per-agent pills for the ingest conversation. Lead and
 * active/waiting children stay as pills; finished and not-started children
 * collapse into an "Other agents" dropdown so the strip stays readable.
 */
export function AgentPillStrip({ pills, selectedKey, onSelect }: AgentPillStripProps) {
  if (pills.length === 0) return null

  const active = pills.filter((p) => isActivePill(p, selectedKey))
  const collapsed = pills.filter((p) => !isActivePill(p, selectedKey))
  const selectedCollapsed = collapsed.find((p) => p.key === selectedKey)
  const lead = active.filter((p) => p.key === LEAD_PILL_KEY)
  const others = active.filter((p) => p.key !== LEAD_PILL_KEY)

  return (
    <div className="flex w-full shrink-0 flex-wrap items-center gap-1.5 border-b border-border px-3 py-2">
      <Tabs value={selectedKey} onValueChange={onSelect} className="min-w-0 flex-1 gap-0">
        <TabsList
          aria-label="Agents in this ingest session"
          variant="line"
          className="flex h-auto w-full flex-wrap items-center justify-start gap-1.5 rounded-none border-0 bg-transparent p-0 group-data-[orientation=horizontal]/tabs:h-auto"
        >
          {lead.map((pill) => (
            <PillTrigger key={pill.key} pill={pill} selectedKey={selectedKey} />
          ))}
          {others.length > 0 ? (
            <span className="mx-0.5 h-4 w-px shrink-0 bg-border" aria-hidden />
          ) : null}
          {others.map((pill) => (
            <PillTrigger key={pill.key} pill={pill} selectedKey={selectedKey} />
          ))}
        </TabsList>
      </Tabs>

      {collapsed.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="xs"
              className={cn(
                'h-auto gap-1 px-2 py-1 text-xs',
                selectedCollapsed && 'border-primary/40 bg-primary/10'
              )}
            >
              {selectedCollapsed ? selectedCollapsed.label : `Other agents (${collapsed.length})`}
              <ChevronDown className="size-3 opacity-70" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-72 min-w-[12rem] overflow-y-auto">
            {collapsed.map((pill) => (
              <DropdownMenuItem
                key={pill.key}
                className="gap-2 text-xs"
                onSelect={() => onSelect(pill.key)}
              >
                <PillIcon pill={pill} />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className={cn('min-w-0 truncate', statusLabelClass(pill.status))}>
                    {pill.label}
                  </span>
                  {isSubagent(pill) ? (
                    <span className="text-10 text-muted-foreground">{subagentKindLabel(pill)}</span>
                  ) : null}
                </span>
                {isTerminal(pill.status) ? (
                  <span className="text-10 text-muted-foreground">{pill.status}</span>
                ) : null}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  )
}
